import { useMutation, useQuery } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { apiClient, unwrap } from '@/lib/api-client';
import type { SelectParams } from '@/types/api/select';
import { createCrudHooks } from './create-crud-hooks';

export const {
  keys: userKeys,
  useList: useUsers,
  useInfiniteList: useInfiniteUsers,
  useDetail: useUser,
  useCreate: useCreateUser,
  useUpdate: useUpdateUser,
  useDelete: useDeleteUser,
} = createCrudHooks(apiClient.users, 'users');

/** Reset de senha (admin) — gera e retorna uma senha temporária, mostrada uma única vez. */
export function useResetUserPassword() {
  const toast = useToast();

  return useMutation({
    mutationFn: async (userId: string) => {
      const res = await (apiClient.users({ id: userId }) as any)['reset-password'].post();
      return unwrap(res.data) as { password: string };
    },
    onError: (e: any) => {
      toast.error(e.message ?? 'Não foi possível resetar a senha.');
    },
  });
}

export type UserOption = { id: string; name: string };

/** Opções para o `AsyncSelect` de responsável (só admin lista usuários). */
export const createUserOptionsQuery = () => {
  return (params?: SelectParams) =>
    useQuery<UserOption[], Error>({
      queryKey: ['users', 'options', params?.search ?? ''],
      queryFn: async () => {
        const res = await apiClient.users.get({
          query: {
            search: params?.search || undefined,
            isActive: 'true',
            orderBy: 'name',
            order: 'asc',
            perPage: 30,
          },
        });
        return (res.data?.data ?? []).map(({ id, name }) => ({ id, name }));
      },
      staleTime: 30_000,
    });
};
